import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2, Save, IdCard } from 'lucide-react';
import { ETHNICITY_OPTIONS } from '../../lib/ethnicity';

/**
 * Iter-281 Phase B Part 3 — Admin Identity Editor
 *
 * Surgical editor for a talent's identity fields. Calls
 * PATCH /api/admin/talents/{user_id}/identity which:
 *  - rejects any name containing '@'
 *  - rejects blank first/last names
 *  - never triggers GPS-consent acceptance or draft-restore side effects
 *  - logs every change to activity_log
 *
 * Intentionally manual-save (no autosave). The admin must click "Save".
 */
const FIELDS = [
  { key: 'first_name',  label: 'First Name',       placeholder: 'Required' },
  { key: 'middle_name', label: 'Middle Name',      placeholder: 'Optional' },
  { key: 'last_name',   label: 'Last Name',        placeholder: 'Required' },
  { key: 'nickname',    label: 'Nickname / Preferred Display', placeholder: 'Optional' },
  // ethnicity intentionally not in this map — rendered as a <select>
];

const AdminIdentityEditor = ({ userId, initial, onSaved }) => {
  const [form, setForm] = useState({
    first_name: '', middle_name: '', last_name: '', nickname: '', ethnicity: '',
  });
  const [original, setOriginal] = useState(form);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = {
      first_name:  initial?.first_name  || '',
      middle_name: initial?.middle_name || '',
      last_name:   initial?.last_name   || '',
      nickname:    initial?.nickname    || '',
      ethnicity:   initial?.ethnicity   || '',
    };
    setForm(next);
    setOriginal(next);
  }, [initial]);

  const dirty = Object.keys(form).some((k) => (form[k] || '') !== (original[k] || ''));

  const handleSave = async () => {
    // Build patch — only changed fields
    const patch = {};
    for (const k of Object.keys(form)) {
      if ((form[k] || '') !== (original[k] || '')) patch[k] = form[k] || null;
    }
    if (!Object.keys(patch).length) return;

    // Client-side guards mirroring the server — ONLY validate fields the
    // admin is actively editing. The server enforces the same rules and
    // is the source of truth; we skip blanket required-field checks so an
    // admin can edit ethnicity/middle/nickname on a corrupted profile
    // whose last_name is already blank (the population this editor exists
    // to clean up).
    for (const f of ['first_name', 'middle_name', 'last_name', 'nickname']) {
      if (f in patch && patch[f] && patch[f].includes('@')) {
        toast.error(`${f.replace('_', ' ')} cannot contain '@'`);
        return;
      }
    }
    if ('first_name' in patch && (!patch.first_name || !patch.first_name.trim())) {
      toast.error('First name cannot be blank');
      return;
    }
    if ('last_name' in patch && (!patch.last_name || !patch.last_name.trim())) {
      toast.error('Last name cannot be blank');
      return;
    }

    setSaving(true);
    try {
      const res = await api.patch(`/admin/talents/${userId}/identity`, patch);
      toast.success(`Identity updated: ${res.data.diff}`);
      const refreshed = {
        first_name:  res.data.first_name  || '',
        middle_name: res.data.middle_name || '',
        last_name:   res.data.last_name   || '',
        nickname:    res.data.nickname    || '',
        ethnicity:   res.data.ethnicity   || '',
      };
      setOriginal(refreshed);
      setForm(refreshed);
      onSaved && onSaved(refreshed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update identity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="admin-identity-editor">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <IdCard className="w-4 h-4 text-cyan-600" />
          Identity Editor
          <span className="text-xs font-normal text-gray-500">
            (admin-only · no autosave · no GPS side-effects)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-1">
              <label
                htmlFor={`identity-${key}`}
                className="text-xs font-medium text-gray-700"
              >
                {label}
              </label>
              <input
                id={`identity-${key}`}
                type="text"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid={`identity-input-${key.replace('_', '-')}`}
              />
            </div>
          ))}
          {/* Iter-282b — ethnicity is a controlled vocabulary <select>.
              Same canonical options the talent self-edit form uses. */}
          <div className="space-y-1 sm:col-span-2">
            <label
              htmlFor="identity-ethnicity"
              className="text-xs font-medium text-gray-700"
            >
              Ethnicity
            </label>
            <select
              id="identity-ethnicity"
              value={form.ethnicity}
              onChange={(e) => setForm((f) => ({ ...f, ethnicity: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="identity-input-ethnicity"
            >
              <option value="">— Not specified —</option>
              {ETHNICITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-gray-500">
            Changes are saved only when you click <strong>Save Identity</strong>.
            Server rejects names containing <code>@</code> or blank first/last names.
          </p>
          <Button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="identity-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save Identity
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminIdentityEditor;
